import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { clientsAPI, ordersAPI, notificationsAPI, appointmentsAPI, referralRewardsAPI, engagementAPI, feedbackAPI, loyaltyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Phone, Mail, MessageSquare, MapPin, CalendarDays, Gift,
  UserPlus, Camera, Upload, Trash2, Pencil, Users, Clock, FileText, Package,
  Ruler, Plus, ShoppingBag, ChevronRight, Archive, Loader2, IndianRupee,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, ExternalLink, Sparkles,
  Heart, Send, Home, Crown,
} from 'lucide-react';
import MeasurementsTab from '@/components/MeasurementsTab';
import { BookAppointmentDialog } from '@/pages/AppointmentsPage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Breadcrumbs } from '@/components/shared/UXComponents';
import { LoyaltyCard, LoyaltyTierBadge } from '@/components/LoyaltyCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_MODE_LABELS = { walk_in: 'Walk-in', domestic_remote: 'Remote', international: 'International' };
const SERVICE_MODE_COLORS = {
  walk_in: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  domestic_remote: { bg: 'rgba(59,130,246,0.08)', text: '#2563EB' },
  international: { bg: 'rgba(139,92,246,0.08)', text: '#7C3AED' },
};

const STATUS_COLORS = {
  draft: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280' },
  confirmed: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB' },
  fabric_ordered: { bg: 'rgba(147,51,234,0.08)', text: '#9333EA' },
  fabric_received: { bg: 'rgba(6,182,212,0.08)', text: '#0891B2' },
  in_production: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  ready_for_trial: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  alterations_in_progress: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  ready_for_delivery: { bg: 'rgba(16,185,129,0.12)', text: '#047857' },
  delivered: { bg: 'rgba(27,42,74,0.08)', text: '#1B2A4A' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
};
const STATUS_LABELS = {
  draft: 'Draft', confirmed: 'Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', in_production: 'In Production',
  ready_for_trial: 'Ready for Trial', alterations_in_progress: 'Alterations',
  ready_for_delivery: 'Ready', delivered: 'Delivered', cancelled: 'Cancelled',
};

const PAY_COLORS = {
  paid: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  partial: { bg: 'rgba(245,158,11,0.08)', text: '#D97706' },
  unpaid: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
};

const APT_TYPE_LABELS = {
  consultation: 'Consultation', measurement: 'Measurement', trial: 'Trial',
  delivery: 'Delivery', alteration_check: 'Alteration', other: 'Other',
};
const APT_TYPE_COLORS = {
  consultation: { bg: 'rgba(59,130,246,0.10)', text: '#2563EB' },
  measurement: { bg: 'rgba(139,92,246,0.10)', text: '#7C3AED' },
  trial: { bg: 'rgba(197,165,90,0.12)', text: '#92700C' },
  delivery: { bg: 'rgba(16,185,129,0.10)', text: '#059669' },
  alteration_check: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  other: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280' },
};

const LOCATION_LABELS = { colaba_store: 'Colaba Store', client_address: 'Client Address', popup_venue: 'Pop-Up', video_call: 'Video Call' };
const COMPLEXION_UNDERTONES = ['warm', 'cool', 'neutral'];
const COMPLEXION_DEPTHS = ['light', 'medium', 'deep'];
const FACE_SHAPES = ['oval', 'round', 'square', 'oblong', 'heart', 'diamond', 'triangular'];
const BODY_SHAPES = ['athletic', 'slim', 'stocky', 'tall_lean', 'heavy_set', 'proportional'];
const COLOUR_PREFS = ['bright', 'pastel', 'earth_tones', 'neutrals', 'monochrome', 'adventurous'];
const PATTERN_COMFORTS = ['solids_only', 'subtle_textures', 'small_checks', 'bold_patterns'];
const PHOTO_TYPES = ['intake_full_length', 'intake_face', 'trial', 'delivery', 'other'];
const PHOTO_TYPE_LABELS = { intake_full_length: 'Full Length', intake_face: 'Face', trial: 'Trial', delivery: 'Delivery', other: 'Other' };
const GARMENT_LABELS = { jacket: 'Suit', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani', bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama', waistcoat: 'Waistcoat' };
const CURRENCY_SYM = { INR: '\u20B9', AED: 'AED ', USD: '$', GBP: '\u00A3' };

const SECTIONS = [
  { id: 'active_orders', label: 'Active Orders', icon: ShoppingBag },
  { id: 'style_insights', label: 'Style Insights', icon: Sparkles },
  { id: 'appointments', label: 'Appointments', icon: CalendarDays },
  { id: 'engagements', label: 'Engagements', icon: Heart },
  { id: 'style', label: 'Style Profile', icon: Pencil },
  { id: 'measurements', label: 'Measurements', icon: Ruler },
  { id: 'history', label: 'Order History', icon: Clock },
  { id: 'communication', label: 'Communication', icon: MessageSquare },
  { id: 'photos', label: 'Photos', icon: Camera },
  { id: 'referrals', label: 'Referrals', icon: Users },
  { id: 'notes', label: 'Notes', icon: FileText },
];

const PRODUCTION_SECTIONS = [
  { id: 'active_orders', label: 'Active Orders', icon: ShoppingBag },
  { id: 'measurements', label: 'Measurements', icon: Ruler },
];

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; }
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLimited = user?.role === 'Production' || user?.role === 'Karigar';

  const [client, setClient] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('active_orders');

  // Shared data
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState([]);
  const [commLogs, setCommLogs] = useState([]);
  const [feedbackData, setFeedbackData] = useState({ feedbacks: [], avg_rating: null });

  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [clientRes, summaryRes, photosRes, notesRes, commRes, fbRes] = await Promise.all([
        clientsAPI.get(id),
        clientsAPI.profileSummary(id),
        clientsAPI.listPhotos(id),
        clientsAPI.listNotes(id),
        notificationsAPI.getLog({ client_id: id, limit: 100 }),
        feedbackAPI.forClient(id).catch(() => ({ data: { feedbacks: [], avg_rating: null } })),
      ]);
      setClient(clientRes.data);
      setSummary(summaryRes.data);
      setPhotos(photosRes.data || []);
      setNotes(notesRes.data || []);
      setCommLogs(commRes.data || []);
      setFeedbackData(fbRes.data || { feedbacks: [], avg_rating: null });
    } catch {
      toast.error('Failed to load client');
      navigate('/app/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-48" /><Skeleton className="h-36 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
  if (!client) return null;

  const sections = isLimited ? PRODUCTION_SECTIONS : SECTIONS;
  const mostRecentPhoto = photos[0];
  const sm = SERVICE_MODE_COLORS[client.service_mode] || SERVICE_MODE_COLORS.walk_in;
  const clientSince = summary?.first_order_date ? fmtDate(summary.first_order_date) : null;
  const ltv = summary?.lifetime_value || 0;

  return (
    <div className="space-y-5" data-testid="client-detail-page">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[
        { label: 'Clients', href: '/app/clients' },
        { label: `${client.first_name} ${client.last_name}` },
      ]} />

      {/* ============ HEADER ============ */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/clients')} className="h-9 px-2 mt-1">
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
        </Button>
        <div className="flex-1">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            {/* Photo + Name */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border border-border/60" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                {mostRecentPhoto ? (
                  <img src={mostRecentPhoto.photo_url.startsWith('/api') ? `${BACKEND_URL}${mostRecentPhoto.photo_url}` : mostRecentPhoto.photo_url}
                    alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-lg font-bold" style={{ color: 'var(--brand-navy)' }}>
                      {client.first_name?.[0]}{client.last_name?.[0]}
                    </span>
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-semibold truncate" style={{ color: 'var(--brand-navy)' }} data-testid="client-name">
                  {client.first_name} {client.last_name}
                </h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                    <Phone className="w-3 h-3" /> {client.phone}
                  </span>
                  {client.email && <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                    <Mail className="w-3 h-3" /> {client.email}
                  </span>}
                  <Badge className="text-[9px] font-semibold px-1.5 py-0 rounded-md" style={{ backgroundColor: sm.bg, color: sm.text }}>
                    {SERVICE_MODE_LABELS[client.service_mode] || client.service_mode}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <StatPill icon={IndianRupee} label="Lifetime Value" value={ltv > 0 ? `\u20B9${ltv.toLocaleString('en-IN')}` : '\u20B90'} data-testid="stat-ltv" />
              <StatPill icon={ShoppingBag} label="Orders" value={summary?.total_orders || 0} data-testid="stat-orders" />
              {feedbackData.avg_rating && (
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.08)' }} data-testid="stat-rating">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <span className="text-xs font-semibold" style={{ color: '#D97706' }}>{feedbackData.avg_rating}</span>
                  <span className="text-[9px]" style={{ color: '#D97706' }}>({feedbackData.total_reviews})</span>
                </div>
              )}
              <StatPill icon={CalendarDays} label={clientSince ? 'Client Since' : 'Status'}
                value={clientSince || 'New Client'} data-testid="stat-since" />
            </div>
          </div>

          {/* Quick Actions */}
          {!isLimited && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="quick-actions">
              <Button size="sm" className="gap-1.5 h-8 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
                onClick={() => setBookingOpen(true)} data-testid="quick-book-appointment">
                <CalendarDays className="w-3 h-3" /> Book Appointment
              </Button>
              <Link to={`/app/orders/new?client=${id}`}>
                <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs rounded-lg" data-testid="quick-new-order">
                  <Plus className="w-3 h-3" /> New Order
                </Button>
              </Link>
              <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs rounded-lg"
                onClick={() => setActiveSection('communication')} data-testid="quick-send-message">
                <MessageSquare className="w-3 h-3" /> Send Message
              </Button>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/clients/${id}/edit`)} className="h-8 text-xs rounded-lg gap-1">
                <Pencil className="w-3 h-3" /> Edit
              </Button>
              {user?.role === 'Admin' && (
                <Button variant="outline" size="sm" onClick={() => setDeleteDialogOpen(true)}
                  className="h-8 text-xs rounded-lg gap-1" style={{ borderColor: 'rgba(220,38,38,0.3)', color: '#DC2626' }}
                  data-testid="delete-client-btn">
                  <Archive className="w-3 h-3" /> Archive
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============ LOYALTY CARD ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {/* Placeholder for future widgets - currently we just let loyalty take full width on mobile */}
        </div>
        <div>
          <LoyaltyCard clientId={id} />
        </div>
      </div>

      {/* ============ SECTION NAV ============ */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 sticky top-0 z-10 bg-[color:var(--bg)] pt-1" data-testid="section-nav">
        {sections.map(sec => {
          const Icon = sec.icon;
          const isActive = activeSection === sec.id;
          return (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors
                ${isActive ? 'bg-white border border-border/70 shadow-sm' : 'hover:bg-white/50'}`}
              style={{ color: isActive ? 'var(--brand-navy)' : 'var(--ink-muted)' }}
              data-testid={`client-tab-${sec.id}`}>
              <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />{sec.label}
              {sec.id === 'active_orders' && summary?.active_orders?.length > 0 && (
                <span className="min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                  style={{ backgroundColor: 'var(--brand-navy)' }}>{summary.active_orders.length}</span>
              )}
              {sec.id === 'appointments' && summary?.upcoming_appointments?.length > 0 && (
                <span className="min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                  style={{ backgroundColor: '#2563EB' }}>{summary.upcoming_appointments.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ============ SECTION: ACTIVE ORDERS ============ */}
      {activeSection === 'active_orders' && (
        <ActiveOrdersSection orders={summary?.active_orders || []} clientId={id} />
      )}

      {/* ============ SECTION: STYLE INSIGHTS ============ */}
      {activeSection === 'style_insights' && !isLimited && (
        <StyleInsightsSection clientId={id} orders={[...(summary?.active_orders || []), ...(summary?.past_orders || [])]} />
      )}

      {/* ============ SECTION: APPOINTMENTS ============ */}
      {activeSection === 'appointments' && !isLimited && (
        <AppointmentsSection appointments={summary?.upcoming_appointments || []} clientId={id}
          onBook={() => setBookingOpen(true)} />
      )}

      {/* ============ SECTION: ENGAGEMENTS ============ */}
      {activeSection === 'engagements' && !isLimited && (
        <EngagementsSection clientId={id} />
      )}

      {/* ============ SECTION: STYLE PROFILE ============ */}
      {activeSection === 'style' && !isLimited && (
        <StyleProfileSection client={client} wardrobe={summary?.wardrobe_summary || []}
          onSaved={fetchAll} clientId={id} />
      )}

      {/* ============ SECTION: MEASUREMENTS ============ */}
      {activeSection === 'measurements' && <MeasurementsTab clientId={id} />}

      {/* ============ SECTION: ORDER HISTORY ============ */}
      {activeSection === 'history' && !isLimited && (
        <OrderHistorySection orders={summary?.past_orders || []} clientId={id} />
      )}

      {/* ============ SECTION: COMMUNICATION ============ */}
      {activeSection === 'communication' && !isLimited && (
        <CommunicationSection clientId={id} logs={commLogs} appointments={summary?.upcoming_appointments || []}
          notes={notes} onRefresh={fetchAll} />
      )}

      {/* ============ SECTION: PHOTOS ============ */}
      {activeSection === 'photos' && !isLimited && (
        <PhotosSection clientId={id} photos={photos} onRefresh={fetchAll} />
      )}

      {/* ============ SECTION: REFERRALS ============ */}
      {activeSection === 'referrals' && !isLimited && (
        <ReferralsSection client={client} summary={summary} />
      )}

      {/* ============ SECTION: NOTES ============ */}
      {activeSection === 'notes' && !isLimited && (
        <NotesSection clientId={id} notes={notes} onRefresh={fetchAll} />
      )}

      {/* Booking Dialog */}
      <BookAppointmentDialog open={bookingOpen} onOpenChange={setBookingOpen}
        initialDate={new Date().toISOString().split('T')[0]}
        prefillClientId={id} onCreated={() => { setBookingOpen(false); fetchAll(); }} />

      {/* Archive Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="delete-client-dialog">
          <DialogHeader><DialogTitle style={{ color: '#DC2626' }}>Archive Client</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            This will archive <strong>{client.first_name} {client.last_name}</strong> and hide them from active lists.
          </p>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={deleting} data-testid="confirm-delete-client-btn" style={{ backgroundColor: '#DC2626' }}
              onClick={async () => {
                setDeleting(true);
                try { await clientsAPI.softDelete(id); toast.success('Client archived'); navigate('/app/clients'); }
                catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                setDeleting(false);
              }}>{deleting ? 'Archiving...' : 'Archive Client'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========== Stat Pill ========== */
function StatPill({ icon: Icon, label, value, ...props }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border/60 bg-white" {...props}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--brand-gold)' }} />
      <div>
        <p className="text-[9px] font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>{label}</p>
        <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{value}</p>
      </div>
    </div>
  );
}

/* ========== SECTION 2: Active Orders ========== */
function ActiveOrdersSection({ orders, clientId }) {
  if (orders.length === 0) {
    return (
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="active-orders-section">
        <CardContent className="py-10 text-center">
          <ShoppingBag className="w-10 h-10 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No active orders</p>
          <Link to={`/app/orders/new?client=${clientId}`}>
            <Button size="sm" className="mt-3 gap-1.5 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="create-order-empty-btn">
              <Plus className="w-3 h-3" /> Create New Order
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="active-orders-section">
      {orders.map(o => {
        const sc = STATUS_COLORS[o.status] || STATUS_COLORS.draft;
        const pc = PAY_COLORS[o.payment_status] || PAY_COLORS.unpaid;
        const cs = CURRENCY_SYM[o.currency] || '';
        const daysRemaining = o.days_remaining;
        const isUrgent = daysRemaining !== null && daysRemaining !== undefined && daysRemaining <= 7 && daysRemaining >= 0;
        const isOverdue = daysRemaining !== null && daysRemaining !== undefined && daysRemaining < 0;

        return (
          <Link key={o.id} to={`/app/orders/${o.id}`} data-testid={`active-order-${o.id}`}>
            <Card className="rounded-xl border border-border/80 bg-white hover:shadow-md cursor-pointer" style={{ transition: 'box-shadow 150ms' }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: sc.bg }}>
                      <Package className="w-5 h-5" style={{ color: sc.text }} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{o.order_number}</p>
                        <Badge className="text-[9px] font-semibold px-1.5 py-0 rounded-md" style={{ backgroundColor: sc.bg, color: sc.text }}>
                          {STATUS_LABELS[o.status] || o.status}
                        </Badge>
                      </div>
                      <p className="text-xs truncate" style={{ color: 'var(--ink-muted)' }}>{o.garment_summary}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {o.required_by_date && (
                      <div className="text-right">
                        <p className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Due</p>
                        <p className={`text-xs font-semibold ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : ''}`}
                          style={!isOverdue && !isUrgent ? { color: 'var(--navy-ink)' } : {}}>
                          {o.required_by_date}
                          {isOverdue && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}
                        </p>
                        {daysRemaining !== null && daysRemaining !== undefined && (
                          <p className="text-[9px]" style={{ color: isOverdue ? '#DC2626' : isUrgent ? '#D97706' : 'var(--ink-muted)' }}>
                            {isOverdue ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d left`}
                          </p>
                        )}
                      </div>
                    )}
                    <Badge className="text-[9px] font-semibold px-1.5 py-0 rounded-md capitalize" style={{ backgroundColor: pc.bg, color: pc.text }}>
                      {o.payment_status}
                    </Badge>
                    <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                      {cs}{o.total_price?.toLocaleString('en-IN')}
                    </span>
                    <ChevronRight className="w-4 h-4 opacity-30" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

/* ========== SECTION 3: Upcoming Appointments ========== */
function AppointmentsSection({ appointments, clientId, onBook }) {
  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="appointments-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Upcoming Appointments</CardTitle>
          <Button size="sm" className="gap-1.5 h-8 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
            onClick={onBook} data-testid="book-appointment-from-section">
            <Plus className="w-3 h-3" /> Book Appointment
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {appointments.length === 0 ? (
          <div className="py-6 text-center">
            <CalendarDays className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No upcoming appointments</p>
          </div>
        ) : (
          <div className="space-y-2">
            {appointments.map(a => {
              const tc = APT_TYPE_COLORS[a.appointment_type] || APT_TYPE_COLORS.other;
              return (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)', borderLeft: `3px solid ${tc.text}` }}
                  data-testid={`upcoming-apt-${a.id}`}>
                  <div className="text-center w-12 flex-shrink-0">
                    <p className="text-lg font-bold leading-none" style={{ color: 'var(--brand-navy)' }}>{a.date?.split('-')[2]}</p>
                    <p className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>
                      {a.date ? new Date(a.date + 'T00:00').toLocaleDateString('en-IN', { month: 'short' }) : ''}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: tc.bg, color: tc.text }}>
                        {APT_TYPE_LABELS[a.appointment_type] || a.appointment_type}
                      </Badge>
                      <span className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>{a.start_time} - {a.end_time}</span>
                    </div>
                    <p className="text-[10px] mt-0.5 flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                      <MapPin className="w-2.5 h-2.5" /> {LOCATION_LABELS[a.location] || a.location}
                    </p>
                  </div>
                  <Badge className="text-[9px] px-1.5 py-0 capitalize" style={{ backgroundColor: 'rgba(59,130,246,0.08)', color: '#2563EB' }}>
                    {a.status}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ========== SECTION 4: Style Profile ========== */
function StyleProfileSection({ client, wardrobe, onSaved, clientId }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      complexion_undertone: client.complexion_undertone || '',
      complexion_depth: client.complexion_depth || '',
      face_shape: client.face_shape || '',
      body_shape: client.body_shape || '',
      colour_preference: client.colour_preference || '',
      pattern_comfort: client.pattern_comfort || '',
      style_notes: client.style_notes || '',
    });
  }, [client]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) { payload[k] = v || null; }
      await clientsAPI.update(clientId, payload);
      toast.success('Style profile updated');
      setEditing(false);
      onSaved();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="style-profile-section">
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Style Profile</CardTitle>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="h-8 text-xs rounded-lg gap-1">
              <Pencil className="w-3 h-3" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" className="h-8 text-xs gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
                onClick={save} disabled={saving}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-4">
            <StyleField label="Complexion Undertone" value={editing ? form.complexion_undertone : client.complexion_undertone}
              options={COMPLEXION_UNDERTONES} editing={editing} onChange={v => setForm(p => ({ ...p, complexion_undertone: v }))} />
            <StyleField label="Complexion Depth" value={editing ? form.complexion_depth : client.complexion_depth}
              options={COMPLEXION_DEPTHS} editing={editing} onChange={v => setForm(p => ({ ...p, complexion_depth: v }))} />
            <StyleField label="Face Shape" value={editing ? form.face_shape : client.face_shape}
              options={FACE_SHAPES} editing={editing} onChange={v => setForm(p => ({ ...p, face_shape: v }))} />
            <StyleField label="Body Shape" value={editing ? form.body_shape : client.body_shape}
              options={BODY_SHAPES} editing={editing} onChange={v => setForm(p => ({ ...p, body_shape: v }))} />
            <StyleField label="Colour Preference" value={editing ? form.colour_preference : client.colour_preference}
              options={COLOUR_PREFS} editing={editing} onChange={v => setForm(p => ({ ...p, colour_preference: v }))} />
            <StyleField label="Pattern Comfort" value={editing ? form.pattern_comfort : client.pattern_comfort}
              options={PATTERN_COMFORTS} editing={editing} onChange={v => setForm(p => ({ ...p, pattern_comfort: v }))} />
          </div>
          <Separator className="my-4 opacity-60" />
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Style Notes</Label>
            {editing ? (
              <textarea value={form.style_notes} onChange={e => setForm(p => ({ ...p, style_notes: e.target.value }))}
                className="w-full h-24 px-3 py-2 mt-1 rounded-lg border border-border/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]"
                placeholder="Observations about preferences..." />
            ) : (
              <p className="text-sm mt-1" style={{ color: client.style_notes ? 'var(--navy-ink)' : 'var(--ink-muted)' }}>
                {client.style_notes || 'No style notes yet.'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Wardrobe Summary */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="wardrobe-summary">
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Wardrobe Summary</CardTitle>
          <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Auto-generated from completed orders</p>
        </CardHeader>
        <CardContent>
          {wardrobe.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: 'var(--ink-muted)' }}>No completed orders yet — wardrobe will populate as orders are delivered.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {wardrobe.map(w => (
                <div key={w.type} className="px-3 py-2 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)', backgroundColor: 'rgba(27,42,74,0.02)' }}>
                  <p className="text-xs font-semibold capitalize" style={{ color: 'var(--brand-navy)' }}>
                    {w.count} {GARMENT_LABELS[w.type] || w.type}{w.count > 1 ? 's' : ''}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                    {w.details.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ========== SECTION 6: Order History ========== */
function OrderHistorySection({ orders, clientId }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-3" data-testid="order-history-section">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>Order History ({orders.length})</h3>
        <Link to={`/app/orders/new?client=${clientId}`}>
          <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs rounded-lg"><Plus className="w-3 h-3" /> New Order</Button>
        </Link>
      </div>
      {orders.length === 0 ? (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="py-8 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No past orders</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {orders.map(o => {
            const sc = STATUS_COLORS[o.status] || STATUS_COLORS.draft;
            const cs = CURRENCY_SYM[o.currency] || '';
            const isExpanded = expanded === o.id;
            return (
              <Card key={o.id} className="rounded-xl border border-border/80 bg-white shadow-sm">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : o.id)} data-testid={`history-order-${o.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-1 h-10 rounded-full" style={{ backgroundColor: sc.text }} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Link to={`/app/orders/${o.id}`} onClick={e => e.stopPropagation()}
                            className="text-sm font-semibold hover:underline" style={{ color: 'var(--brand-navy)' }}>
                            {o.order_number}
                          </Link>
                          <Badge className="text-[9px] px-1.5 py-0 rounded-md" style={{ backgroundColor: sc.bg, color: sc.text }}>
                            {STATUS_LABELS[o.status] || o.status}
                          </Badge>
                        </div>
                        <p className="text-xs truncate" style={{ color: 'var(--ink-muted)' }}>
                          {o.order_date} · {o.garment_summary}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                        {cs}{o.total_price?.toLocaleString('en-IN')}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 opacity-40" /> : <ChevronDown className="w-4 h-4 opacity-40" />}
                    </div>
                  </div>
                  {isExpanded && o.items?.length > 0 && (
                    <div className="px-4 pb-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                      <div className="space-y-2 pt-2">
                        {o.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                              <Package className="w-4 h-4" style={{ color: 'var(--brand-navy)', opacity: 0.5 }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium capitalize" style={{ color: 'var(--navy-ink)' }}>
                                {GARMENT_LABELS[item.garment_type] || item.garment_type}
                              </p>
                              <p className="text-[10px] truncate" style={{ color: 'var(--ink-muted)' }}>
                                {[item.fabric_code, item.fabric_colour, item.fabric_name].filter(Boolean).join(' · ') || 'No fabric details'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 mt-2 pt-2 border-t" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                        <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          Paid: {cs}{o.amount_paid?.toLocaleString('en-IN')} / {cs}{o.total_price?.toLocaleString('en-IN')}
                        </span>
                        <Link to={`/app/orders/${o.id}`} className="text-[10px] flex items-center gap-0.5 ml-auto"
                          style={{ color: 'var(--brand-navy)' }}>
                          View Order <ExternalLink className="w-2.5 h-2.5" />
                        </Link>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ========== SECTION 7: Communication (Unified Timeline) ========== */
const TRIGGER_LABELS_MAP = {
  order_confirmed: 'Order Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', production_started: 'Production Started',
  ready_for_trial: 'Ready for Trial', alterations_complete: 'Alterations Complete',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered',
  payment_received: 'Payment Received', delay_alert: 'Delay Alert',
  birthday_greeting: 'Birthday', feedback_request: 'Feedback',
  appointment_reminder_24h: 'Appt Reminder 24h', appointment_reminder_2h: 'Appt Reminder 2h',
};
const NOTIF_STATUS_COLORS = {
  pending: { bg: 'rgba(245,158,11,0.08)', text: '#D97706' },
  sent: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  delivered: { bg: 'rgba(59,130,246,0.08)', text: '#2563EB' },
  failed: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
  clicked: { bg: 'rgba(139,92,246,0.08)', text: '#7C3AED' },
  scheduled: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280' },
};

function CommunicationSection({ clientId, logs, appointments, notes, onRefresh }) {
  // Build unified timeline
  const timeline = useMemo(() => {
    const items = [];
    // Notification logs
    for (const log of logs) {
      items.push({
        type: 'notification', id: `n-${log.id}`,
        date: log.created_at || '', ...log,
      });
    }
    // Appointments
    for (const apt of appointments) {
      items.push({
        type: 'appointment', id: `a-${apt.id}`,
        date: apt.created_at || apt.date || '', ...apt,
      });
    }
    // Notes
    for (const note of notes) {
      items.push({
        type: 'note', id: `no-${note.id}`,
        date: note.created_at || '', ...note,
      });
    }
    items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return items;
  }, [logs, appointments, notes]);

  const fmtTs = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
  };

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="communication-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Communication Timeline</CardTitle>
          <Badge variant="outline" className="text-xs">{timeline.length} events</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {timeline.length === 0 ? (
          <div className="py-8 text-center">
            <MessageSquare className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No communication history yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {timeline.map(item => {
              if (item.type === 'notification') {
                const nsc = NOTIF_STATUS_COLORS[item.status] || NOTIF_STATUS_COLORS.pending;
                return (
                  <div key={item.id} className="flex gap-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.06)' }}
                    data-testid={`timeline-notif-${item.id}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}>
                      <MessageSquare className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: 'rgba(27,42,74,0.05)', color: 'var(--brand-navy)' }}>
                          {TRIGGER_LABELS_MAP[item.trigger_event] || item.trigger_event}
                        </Badge>
                        <Badge className="text-[8px] px-1 py-0" style={{ backgroundColor: nsc.bg, color: nsc.text }}>{item.status}</Badge>
                        <span className="text-[9px] ml-auto" style={{ color: 'var(--ink-muted)' }}>{fmtTs(item.date)}</span>
                      </div>
                      {item.message_text && <p className="text-[11px] leading-relaxed truncate" style={{ color: 'var(--navy-ink)' }}>{item.message_text}</p>}
                    </div>
                  </div>
                );
              }
              if (item.type === 'appointment') {
                const atc = APT_TYPE_COLORS[item.appointment_type] || APT_TYPE_COLORS.other;
                return (
                  <div key={item.id} className="flex gap-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.06)' }}
                    data-testid={`timeline-apt-${item.id}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: atc.bg }}>
                      <CalendarDays className="w-3.5 h-3.5" style={{ color: atc.text }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: atc.bg, color: atc.text }}>
                          {APT_TYPE_LABELS[item.appointment_type]}
                        </Badge>
                        <span className="text-[9px] ml-auto" style={{ color: 'var(--ink-muted)' }}>{fmtTs(item.date)}</span>
                      </div>
                      <p className="text-[11px]" style={{ color: 'var(--navy-ink)' }}>
                        {item.date?.split('T')[0]} at {item.start_time} · {LOCATION_LABELS[item.location] || item.location}
                      </p>
                    </div>
                  </div>
                );
              }
              // Note
              return (
                <div key={item.id} className="flex gap-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.06)' }}
                  data-testid={`timeline-note-${item.id}`}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(245,158,11,0.08)' }}>
                    <FileText className="w-3.5 h-3.5" style={{ color: '#D97706' }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: 'rgba(245,158,11,0.08)', color: '#D97706' }}>Note</Badge>
                      <span className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>{item.created_by_name}</span>
                      <span className="text-[9px] ml-auto" style={{ color: 'var(--ink-muted)' }}>{fmtTs(item.date)}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed" style={{ color: 'var(--navy-ink)' }}>{item.note_text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ========== SECTION 8: Photos ========== */
function PhotosSection({ clientId, photos, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('other');
  const [viewTab, setViewTab] = useState('all'); // 'all' or 'tryon'

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await clientsAPI.uploadPhoto(clientId, file, uploadType, '');
      toast.success('Photo uploaded');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (photoId) => {
    try { await clientsAPI.deletePhoto(photoId); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed'); }
  };

  // Split photos into regular and try-on
  const regularPhotos = photos.filter(p => p.photo_type !== 'tryon');
  const tryonPhotos = photos.filter(p => p.photo_type === 'tryon');
  const displayPhotos = viewTab === 'all' ? photos : (viewTab === 'tryon' ? tryonPhotos : regularPhotos);

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="photos-section">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Photos ({photos.length})</CardTitle>
          {/* Tab buttons */}
          <div className="flex gap-1 mt-2">
            <button
              onClick={() => setViewTab('all')}
              className={`px-2 py-1 text-[10px] rounded-md transition-colors ${viewTab === 'all' ? 'bg-[var(--brand-navy)] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              All ({photos.length})
            </button>
            <button
              onClick={() => setViewTab('regular')}
              className={`px-2 py-1 text-[10px] rounded-md transition-colors ${viewTab === 'regular' ? 'bg-[var(--brand-navy)] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Regular ({regularPhotos.length})
            </button>
            {tryonPhotos.length > 0 && (
              <button
                onClick={() => setViewTab('tryon')}
                className={`px-2 py-1 text-[10px] rounded-md transition-colors ${viewTab === 'tryon' ? 'bg-[var(--brand-gold)] text-white' : 'bg-amber-50 hover:bg-amber-100 text-amber-800'}`}
              >
                AI Try-On ({tryonPhotos.length})
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={uploadType} onValueChange={setUploadType}>
            <SelectTrigger className="h-8 w-32 text-xs rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PHOTO_TYPES.map(pt => <SelectItem key={pt} value={pt} className="text-xs">{PHOTO_TYPE_LABELS[pt]}</SelectItem>)}
            </SelectContent>
          </Select>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" id="photo-upload-input" />
          <Button variant="outline" size="sm" disabled={uploading} className="h-8 text-xs rounded-lg gap-1"
            onClick={() => document.getElementById('photo-upload-input').click()} data-testid="client-upload-photo-button">
            <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {displayPhotos.length === 0 ? (
          <div className="py-10 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
              {viewTab === 'tryon' ? 'No AI try-on images yet' : 'No photos yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {displayPhotos.map(photo => (
              <div key={photo.id} className={`relative group rounded-lg overflow-hidden border ${photo.photo_type === 'tryon' ? 'border-amber-200 ring-1 ring-amber-100' : 'border-border/60'}`} data-testid="client-photo-item">
                <img src={photo.photo_url.startsWith('/api') ? `${BACKEND_URL}${photo.photo_url}` : photo.photo_url}
                  alt={photo.photo_type} className="w-full h-40 object-cover" />
                {/* Badge area */}
                <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1">
                  <Badge className={`text-[9px] border-0 px-2 py-0.5 ${photo.photo_type === 'tryon' ? 'bg-amber-500 text-white' : 'bg-white/90 text-[#1B2A4A]'}`}>
                    {photo.photo_type === 'tryon' ? 'AI Try-On' : (PHOTO_TYPE_LABELS[photo.photo_type] || photo.photo_type)}
                  </Badge>
                </div>
                {/* Notes tooltip for try-on images */}
                {photo.photo_type === 'tryon' && photo.notes && (
                  <div className="absolute top-2 left-2 right-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[9px] px-2 py-1 rounded bg-black/70 text-white line-clamp-2">{photo.notes}</p>
                  </div>
                )}
                <button onClick={() => handleDelete(photo.id)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-md bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ========== SECTION 9: Referrals ========== */
function ReferralsSection({ client, summary }) {
  const [rewards, setRewards] = useState([]);
  const [loadingRewards, setLoadingRewards] = useState(true);
  
  useEffect(() => {
    if (client?.id) {
      referralRewardsAPI.getUnredeemed(client.id)
        .then(r => setRewards(r.data || []))
        .catch(() => setRewards([]))
        .finally(() => setLoadingRewards(false));
    }
  }, [client?.id]);

  const unredeemedRewards = rewards.filter(r => r.reward_status !== 'redeemed' && r.status !== 'redeemed');
  
  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="referrals-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Referrals</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Unredeemed Rewards Badge */}
        {unredeemedRewards.length > 0 && (
          <div className="p-3 rounded-xl border-2" style={{ borderColor: 'var(--brand-gold)', backgroundColor: 'rgba(197,165,90,0.08)' }}
            data-testid="unredeemed-rewards-badge">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
              <span className="text-xs font-bold" style={{ color: 'var(--brand-gold)' }}>
                {unredeemedRewards.length} UNREDEEMED REWARD{unredeemedRewards.length > 1 ? 'S' : ''}
              </span>
            </div>
            <div className="space-y-2">
              {unredeemedRewards.map(reward => (
                <div key={reward.id} className="flex items-center justify-between p-2 rounded-lg bg-white/60">
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{reward.reward_description}</p>
                    <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                      From referral: {reward.referred_client_name}
                    </p>
                  </div>
                  <Badge className="text-[9px] font-bold" style={{ backgroundColor: 'var(--brand-gold)', color: 'white' }}>
                    Tier {reward.reward_tier || reward.tier}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referred by */}
        <div className="flex items-center gap-3">
          <UserPlus className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Referred By</p>
            {client.referred_by_client_id ? (
              <Link to={`/app/clients/${client.referred_by_client_id}`}
                className="text-sm font-semibold underline decoration-[color:var(--brand-gold)] underline-offset-2 hover:decoration-2"
                style={{ color: 'var(--brand-navy)' }} data-testid="referred-by-link">
                {client.referred_by_name || 'Unknown'}
              </Link>
            ) : (
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>None</p>
            )}
          </div>
        </div>

        <Separator className="opacity-60" />

        {/* Has referred */}
        <div className="flex items-start gap-3">
          <Users className="w-4 h-4 mt-0.5" style={{ color: 'var(--ink-muted)' }} />
          <div className="flex-1">
            <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>
              Has Referred: {summary?.referral_count || 0} clients
            </p>
            {summary?.referred_clients?.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {summary.referred_clients.map(rc => (
                  <div key={rc.id} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                    <Link to={`/app/clients/${rc.id}`}
                      className="text-xs font-medium underline decoration-[color:var(--brand-gold)] underline-offset-2 hover:decoration-2"
                      style={{ color: 'var(--brand-navy)' }}>
                      {rc.first_name} {rc.last_name}
                    </Link>
                    <div className="flex items-center gap-2">
                      {rc.first_order_date && (
                        <Badge variant="outline" className="text-[9px]" style={{ color: '#059669', borderColor: 'rgba(5,150,105,0.3)' }}>
                          Ordered
                        </Badge>
                      )}
                      {rc.total_spent > 0 && (
                        <span className="text-[10px] font-medium tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                          {'\u20B9'}{rc.total_spent?.toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Referral Revenue */}
        {(summary?.referral_revenue || 0) > 0 && (
          <>
            <Separator className="opacity-60" />
            <div className="flex items-center gap-3">
              <IndianRupee className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Revenue from Referrals</p>
                <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                  {'\u20B9'}{summary.referral_revenue.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ========== SECTION 10: Notes ========== */
function NotesSection({ clientId, notes, onRefresh }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await clientsAPI.createNote(clientId, text);
      toast.success('Note added');
      setText('');
      onRefresh();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="notes-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Notes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Add a note..."
            className="flex-1 h-20 px-3 py-2 rounded-lg border border-border/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]"
            data-testid="client-note-input" />
        </div>
        <Button onClick={add} disabled={saving || !text.trim()} className="h-8 text-xs rounded-lg gap-1"
          style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="client-add-note-button">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {saving ? 'Adding...' : 'Add Note'}
        </Button>
        {notes.length > 0 && <Separator className="opacity-60" />}
        <div className="space-y-2">
          {notes.map(note => (
            <div key={note.id} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }} data-testid="client-note-item">
              <p className="text-sm leading-relaxed" style={{ color: 'var(--navy-ink)' }}>{note.note_text}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{note.created_by_name}</span>
                <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>·</span>
                <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{fmtDate(note.created_at)}</span>
              </div>
            </div>
          ))}
          {notes.length === 0 && <p className="text-sm text-center py-4" style={{ color: 'var(--ink-muted)' }}>No notes yet</p>}
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== SECTION: Style Insights (auto-generated from order history) ========== */
function StyleInsightsSection({ clientId, orders }) {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    // Generate insights from order history
    const generateInsights = async () => {
      setLoading(true);
      try {
        // Analyze orders to extract preferences
        const colorCounts = {};
        const styleCounts = {};
        const patternCounts = {};
        const prices = [];
        const garmentTypes = {};

        for (const order of orders) {
          // Get order details if needed
          const items = order.items || [];
          for (const item of items) {
            // Count garment types
            const gt = item.garment_type;
            if (gt) garmentTypes[gt] = (garmentTypes[gt] || 0) + 1;

            // Extract fabric info
            const fabric = item.fabric || {};
            if (fabric.colour) {
              colorCounts[fabric.colour] = (colorCounts[fabric.colour] || 0) + 1;
            }
            if (fabric.pattern) {
              patternCounts[fabric.pattern] = (patternCounts[fabric.pattern] || 0) + 1;
            }

            // Extract customisation specs
            const specs = item.customisation_spec || {};
            if (specs.lapel_style) {
              styleCounts[`${specs.lapel_style} lapel`] = (styleCounts[`${specs.lapel_style} lapel`] || 0) + 1;
            }
            if (specs.button_stance) {
              styleCounts[specs.button_stance] = (styleCounts[specs.button_stance] || 0) + 1;
            }
            if (specs.vent_style) {
              styleCounts[`${specs.vent_style} vent`] = (styleCounts[`${specs.vent_style} vent`] || 0) + 1;
            }
          }

          // Track prices
          if (order.total_price) {
            prices.push(order.total_price);
          }
        }

        // Calculate top preferences
        const sortByCount = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]);
        const topColors = sortByCount(colorCounts).slice(0, 3);
        const topStyles = sortByCount(styleCounts).slice(0, 3);
        const topPatterns = sortByCount(patternCounts).slice(0, 2);
        const topGarments = sortByCount(garmentTypes).slice(0, 3);

        // Price range
        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

        setInsights({
          colors: {
            preferred: topColors.map(([c, count]) => ({ name: c, count })),
            total: Object.values(colorCounts).reduce((a, b) => a + b, 0),
          },
          styles: {
            preferred: topStyles.map(([s, count]) => ({ name: s.replace(/_/g, ' '), count })),
          },
          patterns: {
            preferred: topPatterns.map(([p, count]) => ({ name: p, count })),
          },
          garments: {
            preferred: topGarments.map(([g, count]) => ({ name: g.replace(/_/g, ' '), count })),
          },
          priceRange: { min: minPrice, max: maxPrice },
          totalOrders: orders.length,
        });
      } catch (err) {
        console.error('Failed to generate insights:', err);
      }
      setLoading(false);
    };

    generateInsights();
  }, [orders]);

  if (loading) {
    return (
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="style-insights-section">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" style={{ color: 'var(--brand-gold)' }} />
          <p className="text-sm mt-2" style={{ color: 'var(--ink-muted)' }}>Analyzing style preferences...</p>
        </CardContent>
      </Card>
    );
  }

  if (!insights || orders.length === 0) {
    return (
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="style-insights-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Sparkles className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            Style Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-center">
          <Sparkles className="w-10 h-10 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            Style insights will appear after the client places orders
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="style-insights-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
          <Sparkles className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
          Style Insights
        </CardTitle>
        <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
          Auto-generated from {insights.totalOrders} order{insights.totalOrders !== 1 ? 's' : ''}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Preferred Colours */}
        {insights.colors.preferred.length > 0 && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-muted)' }}>
              Preferred Colours
            </p>
            <div className="flex flex-wrap gap-2">
              {insights.colors.preferred.map((c, i) => (
                <Badge key={i} variant="outline" className="text-xs px-2 py-1 capitalize"
                  style={{ borderColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}>
                  {c.name} <span className="ml-1 opacity-60">({c.count}x)</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Preferred Styles */}
        {insights.styles.preferred.length > 0 && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-muted)' }}>
              Preferred Style
            </p>
            <div className="flex flex-wrap gap-2">
              {insights.styles.preferred.map((s, i) => (
                <Badge key={i} variant="outline" className="text-xs px-2 py-1 capitalize"
                  style={{ borderColor: 'rgba(27,42,74,0.2)', color: 'var(--navy-ink)' }}>
                  {s.name} <span className="ml-1 opacity-60">({s.count}x)</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Pattern Comfort */}
        {insights.patterns.preferred.length > 0 && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-muted)' }}>
              Pattern Comfort
            </p>
            <div className="flex flex-wrap gap-2">
              {insights.patterns.preferred.map((p, i) => (
                <Badge key={i} variant="outline" className="text-xs px-2 py-1 capitalize"
                  style={{ borderColor: 'rgba(27,42,74,0.2)', color: 'var(--navy-ink)' }}>
                  {p.name} <span className="ml-1 opacity-60">({p.count}x)</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Garment Preferences */}
        {insights.garments.preferred.length > 0 && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-muted)' }}>
              Frequent Garments
            </p>
            <div className="flex flex-wrap gap-2">
              {insights.garments.preferred.map((g, i) => (
                <Badge key={i} variant="outline" className="text-xs px-2 py-1 capitalize"
                  style={{ borderColor: 'rgba(27,42,74,0.2)', color: 'var(--navy-ink)' }}>
                  {g.name} <span className="ml-1 opacity-60">({g.count}x)</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Price Range */}
        {insights.priceRange.max > 0 && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(197,165,90,0.06)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: 'var(--ink-muted)' }}>
              Price Range
            </p>
            <p className="text-sm font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
              ₹{insights.priceRange.min.toLocaleString('en-IN')} - ₹{insights.priceRange.max.toLocaleString('en-IN')}
            </p>
            <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>per order</p>
          </div>
        )}

        <p className="text-[9px] text-center pt-2" style={{ color: 'var(--ink-muted)' }}>
          These insights help personalize recommendations without running the Style Advisor
        </p>
      </CardContent>
    </Card>
  );
}

/* ========== Style Field (reused) ========== */
function StyleField({ label, value, options, editing, onChange }) {
  const displayValue = value ? value.replace(/_/g, ' ') : null;
  if (editing) {
    return (
      <div className="space-y-1">
        <Label className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>{label}</Label>
        <Select value={value || 'none'} onValueChange={v => onChange(v === 'none' ? '' : v)}>
          <SelectTrigger className="h-9 rounded-lg text-xs"><SelectValue placeholder="Not set" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not set</SelectItem>
            {options.map(o => <SelectItem key={o} value={o} className="capitalize text-xs">{o.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>{label}</p>
      <p className={`text-sm font-medium capitalize ${displayValue ? '' : 'italic'}`}
        style={{ color: displayValue ? 'var(--navy-ink)' : 'var(--ink-muted)' }}>
        {displayValue || 'Not set'}
      </p>
    </div>
  );
}



/* ============================================================
   ENGAGEMENTS SECTION
   ============================================================ */
const ENGAGEMENT_TYPE_LABELS = {
  feedback_request: 'Feedback Request',
  dry_clean_reminder: 'Dry Clean Reminder',
  garment_care_tip: 'Care Tip',
  reorder_nudge: 'Reorder Suggestion',
  birthday_greeting: 'Birthday',
  anniversary_greeting: 'Anniversary',
};

const ENGAGEMENT_TYPE_COLORS = {
  feedback_request: '#2563EB',
  dry_clean_reminder: '#7C3AED',
  garment_care_tip: '#059669',
  reorder_nudge: '#D97706',
  birthday_greeting: '#EC4899',
  anniversary_greeting: '#EC4899',
};

const ENGAGEMENT_STATUS_STYLES = {
  scheduled: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280', label: 'Scheduled' },
  sent: { bg: 'rgba(16,185,129,0.08)', text: '#059669', label: 'Sent' },
  skipped: { bg: 'rgba(245,158,11,0.08)', text: '#D97706', label: 'Skipped' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', label: 'Cancelled' },
};

function EngagementsSection({ clientId }) {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);

  const fetchEngagements = useCallback(async () => {
    try {
      const res = await engagementAPI.list({ client_id: clientId, limit: 50 });
      setEngagements(res.data || []);
    } catch (err) {
      console.error('Failed to load engagements:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchEngagements();
  }, [fetchEngagements]);

  const handleSend = async (id) => {
    setSending(id);
    try {
      await engagementAPI.send(id);
      toast.success('Engagement marked as sent');
      fetchEngagements();
    } catch {
      toast.error('Failed to send');
    } finally {
      setSending(null);
    }
  };

  const handleSkip = async (id) => {
    try {
      await engagementAPI.skip(id);
      toast.success('Engagement skipped');
      fetchEngagements();
    } catch {
      toast.error('Failed to skip');
    }
  };

  const scheduleBirthday = async () => {
    try {
      await engagementAPI.scheduleBirthday(clientId);
      toast.success('Birthday greeting scheduled');
      fetchEngagements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    }
  };

  const scheduleAnniversary = async () => {
    try {
      await engagementAPI.scheduleAnniversary(clientId);
      toast.success('Anniversary greeting scheduled');
      fetchEngagements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    }
  };

  // Separate scheduled (upcoming) from past
  const scheduled = engagements.filter(e => e.status === 'scheduled');
  const past = engagements.filter(e => e.status !== 'scheduled');

  if (loading) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <div className="space-y-4" data-testid="engagements-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5" style={{ color: '#EC4899' }} />
          <h3 className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>
            Engagement Timeline
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={scheduleBirthday}
            data-testid="schedule-birthday-btn">
            <Gift className="w-3 h-3" /> Schedule Birthday
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={scheduleAnniversary}
            data-testid="schedule-anniversary-btn">
            <Heart className="w-3 h-3" /> Schedule Anniversary
          </Button>
        </div>
      </div>

      {/* Upcoming Engagements */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <CalendarDays className="w-4 h-4" style={{ color: '#2563EB' }} />
            Upcoming ({scheduled.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {scheduled.length === 0 ? (
            <p className="text-xs text-center py-6" style={{ color: 'var(--ink-muted)' }}>
              No upcoming engagements. They are automatically scheduled when orders are delivered.
            </p>
          ) : (
            <div className="space-y-2">
              {scheduled.map(eng => {
                const color = ENGAGEMENT_TYPE_COLORS[eng.engagement_type] || '#6B7280';
                const isToday = eng.scheduled_date === new Date().toISOString().split('T')[0];
                return (
                  <div key={eng.id} className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{ 
                      borderColor: isToday ? color + '40' : 'rgba(27,42,74,0.08)',
                      backgroundColor: isToday ? color + '08' : 'transparent',
                    }}
                    data-testid={`scheduled-engagement-${eng.id}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color + '15' }}>
                      <Heart className="w-5 h-5" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: color + '15', color }}>
                          {ENGAGEMENT_TYPE_LABELS[eng.engagement_type] || eng.engagement_type}
                        </Badge>
                        {isToday && (
                          <Badge className="text-[9px] px-1.5 py-0 font-bold" style={{ backgroundColor: '#EC4899', color: 'white' }}>
                            TODAY
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                        {new Date(eng.scheduled_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      {eng.garment_type && (
                        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          {eng.garment_type.replace('_', ' ')}
                        </p>
                      )}
                    </div>
                    {isToday && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" className="h-7 text-[10px] gap-1" style={{ backgroundColor: color }}
                          onClick={() => handleSend(eng.id)} disabled={sending === eng.id}
                          data-testid={`send-engagement-${eng.id}`}>
                          {sending === eng.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          Sent
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-[10px]"
                          onClick={() => handleSkip(eng.id)} data-testid={`skip-engagement-${eng.id}`}>
                          Skip
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Engagements */}
      {past.length > 0 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Clock className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
              History ({past.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {past.slice(0, 20).map(eng => {
                const color = ENGAGEMENT_TYPE_COLORS[eng.engagement_type] || '#6B7280';
                const statusStyle = ENGAGEMENT_STATUS_STYLES[eng.status] || ENGAGEMENT_STATUS_STYLES.scheduled;
                return (
                  <div key={eng.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50/50 transition-colors"
                    data-testid={`past-engagement-${eng.id}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                          {ENGAGEMENT_TYPE_LABELS[eng.engagement_type] || eng.engagement_type}
                        </span>
                        <Badge variant="outline" className="text-[8px] px-1 py-0"
                          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text, borderColor: 'transparent' }}>
                          {statusStyle.label}
                        </Badge>
                      </div>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                        {new Date(eng.scheduled_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {eng.sent_at && ` · Sent ${new Date(eng.sent_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
